import { useEffect, type RefObject } from 'react';

interface UseKeyboardNavigationOptions {
  minimized: boolean;
  sessionsOpen: boolean;
  closeSessions: () => void;
  closeChat: () => void;
  chatContainerRef: RefObject<HTMLDivElement | null>;
  sessionsPanelRef: RefObject<HTMLDivElement | null>;
  /**
   * Modal (fullscreen) panel: Tab cycles inside the panel and Escape closes from anywhere.
   * Floating panel (`false`): Tab moves freely between the widget and the page, and Escape
   * only closes when it is aimed at the widget (or at nothing in particular), so the host
   * page keeps its own Escape handling. The sessions overlay keeps focus in either mode
   * because it covers the rest of the panel. Defaults to `true`.
   */
  modal?: boolean;
}

/** Keydown aimed at the widget, or at no focused control at all (body/document). */
function targetsWidget(target: EventTarget | null, container: HTMLElement | null): boolean {
  if (
    !(target instanceof Node) ||
    target === document ||
    target === document.body ||
    target === document.documentElement
  ) {
    return true;
  }
  return !!container && container.contains(target);
}

/**
 * Elements that match the focusable selector but cannot actually receive focus:
 * `hidden` (or inside a hidden ancestor), aria-hidden, visually hidden file inputs,
 * and, when the browser exposes layout info, elements without a rendered box.
 */
function isHiddenFromFocus(el: HTMLElement, hasLayout: boolean): boolean {
  if (el.hidden || el.closest('[hidden]')) return true;
  if (el.getAttribute('aria-hidden') === 'true') return true;
  if (
    el instanceof HTMLInputElement &&
    el.type === 'file' &&
    (el.classList.contains('hidden') || el.classList.contains('sr-only'))
  ) {
    return true;
  }
  if (hasLayout && el.offsetParent === null && el.offsetWidth === 0 && el.offsetHeight === 0) {
    return true;
  }
  return false;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([aria-hidden="true"])';
  let candidates: HTMLElement[];
  try {
    candidates = Array.from(container.querySelectorAll<HTMLElement>(selector));
  } catch {
    candidates = Array.from(
      container.querySelectorAll<HTMLElement>(
        'button, a[href], input, select, textarea, [tabindex]'
      )
    );
  }
  // jsdom and similar environments report no layout at all; only trust offsetParent
  // when at least one candidate has a real box.
  const hasLayout = candidates.some(el => el.offsetWidth > 0 || el.offsetHeight > 0);
  return candidates.filter(el => !isHiddenFromFocus(el, hasLayout));
}

export function useKeyboardNavigation({
  minimized,
  sessionsOpen,
  closeSessions,
  closeChat,
  chatContainerRef,
  sessionsPanelRef,
  modal = true,
}: UseKeyboardNavigationOptions): void {
  // Escape to close
  useEffect(() => {
    if (minimized) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing || e.keyCode === 229) return;
      // Something closer to the target (e.g. an open menu) already consumed this Escape
      if (e.defaultPrevented) return;
      if (!modal && !targetsWidget(e.target, chatContainerRef.current)) return;
      if (sessionsOpen) {
        closeSessions();
        return;
      }
      closeChat();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [minimized, closeChat, sessionsOpen, closeSessions, modal, chatContainerRef]);

  // Focus trap: cycle focus within the modal panel, or within the sessions overlay
  useEffect(() => {
    if (minimized || (!modal && !sessionsOpen)) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || e.isComposing || e.keyCode === 229) return;
      // Suspense may mount the sessions panel after this effect, without rerendering us.
      const container = sessionsOpen ? sessionsPanelRef.current : chatContainerRef.current;
      if (!container) return;
      const focusable = getFocusableElements(container);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // If focus is outside the container, pull it back to first element
      if (!container.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [minimized, sessionsOpen, modal, chatContainerRef, sessionsPanelRef]);
}
